import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { StarlinkDemandMap } from '@/components/value-chain/explorer/StarlinkDemandMap';
import { StarlinkIndependentSeries } from '@/components/value-chain/explorer/StarlinkIndependentSeries';
import { StarlinkTradeEvidence } from '@/components/value-chain/explorer/StarlinkTradeEvidence';
import {
  ARPU_PATH,
  CONSUMER_IDENTITY,
  INVESTOR_AS_OF,
  INVESTOR_DOCS,
  INVESTOR_GROUPS,
  INVESTOR_QUESTIONS,
  kindLabel,
  rowsFor,
  type InvestorKind,
} from '@/components/value-chain/starlinkInvestor';
import { STARLINK_TRADE_KPIS } from '@/components/value-chain/starlinkTrade';

const KIND_CLASS: Record<InvestorKind, string> = {
  printed: 'bg-slate-900 text-white',
  independent: 'bg-sky-100 text-sky-800',
  company_report: 'bg-amber-100 text-amber-900',
  hole: 'bg-rose-100 text-rose-800',
};

export function StarlinkInvestorDesk() {
  const identity = CONSUMER_IDENTITY;
  const arpuData = useMemo(() => ARPU_PATH.map((point) => ({ ...point })), []);

  return (
    <div className="space-y-3 pb-8">
      <p className="text-[12px] text-slate-500">{INVESTOR_AS_OF}</p>

      <section className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
        <h2 className="text-[13px] font-semibold text-amber-950">Do not mix these objects</h2>
        <p className="mt-1 text-[12px] leading-relaxed text-amber-900">{identity.caveat}</p>
        <dl className="mt-3 grid gap-2 sm:grid-cols-3">
          <IdentityStat
            label="EoP × ARPU × 3 months"
            value={`$${identity.rolledM.toLocaleString()}M`}
          />
          <IdentityStat label="Printed Consumer Q2" value={`$${identity.printedConsumerM.toLocaleString()}M`} />
          <IdentityStat label="Residual (not inventory)" value={`$${identity.residualM}M`} />
        </dl>
      </section>

      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        {INVESTOR_QUESTIONS.map((question) => (
          <article key={question.id} className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="text-[13px] font-semibold text-slate-900">{question.title}</h3>
            <p className="mt-1.5 text-[12px] leading-relaxed text-slate-500">{question.body}</p>
          </article>
        ))}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <section className="flex h-[280px] flex-col rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="text-[13px] font-semibold text-slate-800">ARPU path ($ / month)</h3>
          <p className="text-[11px] text-slate-400">S-1 annual prints through FY25; Q1/Q2 2026 are period ARPU</p>
          <div className="mt-1 min-h-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={arpuData} barSize={28} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 120]}
                />
                <Tooltip
                  formatter={(value) => [`$${Number(value)}`, 'ARPU']}
                  contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="arpu" fill="#64748b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="text-[13px] font-semibold text-slate-800">Hardware landings (warehouse)</h3>
          <p className="text-[11px] text-slate-400">Independent of Note 3 mix · Connectivity products, not Consumer bills</p>
          <dl className="mt-3 grid grid-cols-2 gap-2">
            {STARLINK_TRADE_KPIS.map((kpi) => (
              <div key={kpi.label} className="rounded-xl bg-slate-50 px-3 py-2.5">
                <dt className="text-[11px] text-slate-500">{kpi.label}</dt>
                <dd className="mt-0.5 text-[18px] font-semibold tracking-tight text-slate-900">{kpi.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <StarlinkIndependentSeries />

      {INVESTOR_GROUPS.filter((group) => group.id !== 'proxies').map((group) => (
        <section key={group.id} className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-[15px] font-semibold text-slate-900">{group.label}</h3>
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] tracking-wide text-slate-400 uppercase">
                <th className="pb-2 pr-3 font-medium">Metric</th>
                <th className="pb-2 pr-3 font-medium">Value</th>
                <th className="pb-2 pr-3 font-medium">Kind</th>
                <th className="pb-2 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {rowsFor(group.id).map((row) => (
                <tr key={row.metric} className="border-b border-slate-100 align-top">
                  <td className="py-2.5 pr-3 text-slate-700">{row.metric}</td>
                  <td className="py-2.5 pr-3 font-semibold text-slate-900">{row.value}</td>
                  <td className="py-2.5 pr-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${KIND_CLASS[row.kind]}`}
                    >
                      {kindLabel(row.kind)}
                    </span>
                  </td>
                  <td className="py-2.5 text-[12px] text-slate-500">{row.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      <StarlinkDemandMap />
      <StarlinkTradeEvidence />

      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-[15px] font-semibold text-slate-900">Primary filings</h3>
        <ul className="divide-y divide-slate-100">
          {INVESTOR_DOCS.map((doc) => (
            <li key={doc.title} className="flex items-start justify-between gap-4 py-3">
              <div>
                {doc.href ? (
                  <a
                    href={doc.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[13px] font-medium text-blue-700 hover:text-blue-900"
                  >
                    {doc.title}
                  </a>
                ) : (
                  <p className="text-[13px] font-medium text-slate-900">{doc.title}</p>
                )}
                <p className="text-[12px] text-slate-500">{doc.detail}</p>
              </div>
              <span className="shrink-0 text-[12px] text-slate-400">{doc.date}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function IdentityStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-white/80 px-3 py-2">
      <dt className="text-[11px] text-amber-800">{label}</dt>
      <dd className="mt-0.5 text-[18px] font-semibold tracking-tight text-amber-950">{value}</dd>
    </div>
  );
}

export function InvestorIdentityBanner() {
  const identity = CONSUMER_IDENTITY;
  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-3xl">
          <h2 className="text-[13px] font-semibold text-amber-950">Consumer identity does not close</h2>
          <p className="mt-1 text-[12px] leading-relaxed text-amber-900">{identity.caveat}</p>
        </div>
        <p className="text-[22px] leading-none font-semibold tracking-tight text-amber-950">
          ${identity.residualM}M
          <span className="mt-1 block text-[11px] font-medium text-amber-800">residual vs printed Consumer</span>
        </p>
      </div>
    </section>
  );
}
